// src/pages/CoursesPage.js

import React, { useState, useEffect } from 'react';
import { Container, Grid, Typography, Box, CircularProgress, Alert } from '@mui/material';
import courseService from '../services/courseService';
import enrollmentService from '../services/enrollmentService'; // Import enrollment service
import paymentService from '../services/paymentService';     // Import payment service
import { useAuth } from '../contexts/AuthContext';           // Import useAuth hook
import CourseCard from '../components/CourseCard';

const CoursesPage = () => {
  const [courses, setCourses] = useState([]);
  const [enrolledCourseIds, setEnrolledCourseIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    const fetchCoursesAndEnrollments = async () => {
      try {
        setLoading(true);
        const [coursesData, enrollmentsData] = await Promise.all([
          courseService.getAllCourses(),
          isAuthenticated ? enrollmentService.getMyEnrollments() : Promise.resolve([])
        ]);

        setCourses(coursesData);

        if (enrollmentsData.length > 0) {
          const enrolledIds = new Set(enrollmentsData.map(enrollment => enrollment.course.id));
          setEnrolledCourseIds(enrolledIds);
        }

      } catch (err) {
        setError('Failed to load courses. Please try again later.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchCoursesAndEnrollments();
  }, [isAuthenticated]);

  const handleEnroll = async (course) => {
    if (!isAuthenticated || !user) {
      alert('Please log in to enroll in a course.');
      // You could also navigate to the login page here
      return;
    }

    try {
      const paymentResult = await paymentService.processPayment(course, user);

      if (paymentResult.success) {
        alert('Payment successful! You are now enrolled.');
        window.location.reload(); // Reload to update enrollment status
      } else {
        throw new Error(paymentResult.message || 'Payment failed.');
      }
    } catch (err) {
      setError(err.message || 'An error occurred during enrollment.');
    }
  };

  const handleViewDetails = (course) => {
    // Implement your view details logic, e.g., navigate to course detail page
    // navigate(`/courses/${course.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-slate-200 border-t-slate-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500 text-sm">Loading courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-50 border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-slate-100 border border-slate-200 mb-4">
              <span className="text-xs font-medium text-slate-700">UGC NET LAW Preparation</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-heading font-bold text-slate-900 mb-4 tracking-tight">
              All Courses
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Browse our comprehensive collection of courses designed for your success
            </p>
          </div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <CourseCard
                key={course.id}
                course={course}
                isEnrolled={enrolledCourseIds.has(course.id)}
                onEnroll={handleEnroll}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CoursesPage;